import { createSelector } from 'reselect';
import createCachedSelector, { LruCacheObject } from 're-reselect';
import * as transactionSelectors from 'data/transactions/selectors';
import { Set, OrderedMap, Map as ImmutableMap, Record, List as ImmutableList, merge } from 'immutable';
import { getCoaId } from 'data/chartOfAccounts/utils';
import { getCoaString } from 'data/categories/retrievers';
import { getAccountsById } from 'data/accounts/selectors';
import { getCategoriesById } from 'data/categories/selectors';
import { CategoryTypeEnum } from 'data/categories/types';
import { CoaTypeEnum } from 'data/chartOfAccounts/types';
import { isSplitTxn } from 'data/transactions/utils';
import { DateTime } from 'luxon';
import { createArraysEqualShallowSelector } from 'utils/selectorsHelpers';

import { FREE_TO_SPEND_REDUCER_KEY } from './freeToSpendReducer';

const getStore = (state) => state[FREE_TO_SPEND_REDUCER_KEY];
export const getLoadPending = (state) => getStore(state).loadPending;
export const isLoading = (state) => getStore(state).isLoading;
export const getLastSyncDate = (state) => getStore(state).lastSyncDate;
export const getFreeToSpendIntervals = (state) => getStore(state).resourcesById;
export const getFreeToSpendForDate = createSelector(
  getFreeToSpendIntervals,
  (state, date) => date,
  (intervals, date) => intervals.find((interval) => interval.date === date),
);

export const earliestIntervalDate = createSelector(
  getFreeToSpendIntervals,
  (intervals) => {
    const minInterval = intervals && intervals.min((interval1, interval2) => DateTime.fromISO(interval1.date) - DateTime.fromISO(interval2.date));
    return minInterval && DateTime.fromISO(minInterval.date);
  }
);

const COANode = Record({
  id: '0',
  parentId: '0',
  coas: Set(),
  transactions: ImmutableMap(),
  balance: 0,
  label: undefined,
  logoUrl: undefined,
});

const updateNodeWithTransaction = (coaNodesTree, id, parentId, label, logoUrl, transaction, amount, coa) => {
  if (assert(id, 'fts node id should be defined')) {
    const coaNode = coaNodesTree.get(id) || new COANode({
      id,
      parentId,
      label,
      logoUrl,
    });
    const updatedNode = coaNode.merge({
      transactions: coaNode.transactions.set(transaction.id, transaction),
      balance: coaNode.balance + Number(amount),
      coas: coaNode.coas.add(coa),
      logoUrl,
    });
    coaNodesTree.set(id, updatedNode);
  }
};

export const getNodesFromTransactions = createCachedSelector(
  getAccountsById, // to force update for getCoaString
  getCategoriesById, // to force update for getCoaString
  (state, transactions) => transactions || transactionSelectors.getTransactionsById(state),
  (state, transactions, maxNodesNum) => maxNodesNum,
  (_accounts, categoriesById, transactionsById, maxNodesNum) => {

    const coaNodesRow = OrderedMap().withMutations((coaNodes) => {
      transactionsById.forEach((transaction) => {
        const items = isSplitTxn(transaction) ? transaction.split.items : [transaction];
        items.forEach((item) => {
          let id = getCoaId(item.coa);
          if (item.coa && item.coa.type === CoaTypeEnum.CATEGORY) {
            let category = categoriesById.get(id);
            if (category && category.type === CategoryTypeEnum.INCOME) {
              return;
            }
            let label = (category && category.name) || 'Unknown';
            const logoUrl = category && category.logoUrl;
            const idExplicit = `${id}-explicit`;
            updateNodeWithTransaction(coaNodes, idExplicit, id, label, logoUrl, transaction, item.amount, item.coa);

            for (let loopProtect = 0; loopProtect < 255 && id !== '0'; loopProtect++) {
              category = categoriesById.get(id);
              const parentId = category && category.parentId || '0';
              label = (category && category.name) || 'Unknown';
              updateNodeWithTransaction(coaNodes, id, parentId, label, logoUrl, transaction, item.amount, item.coa);
              id = parentId;
            }
          } else {
            const label = getCoaString(item.coa, true);
            updateNodeWithTransaction(coaNodes, id, '0', label, undefined, transaction, item.amount, item.coa);
          }
          updateNodeWithTransaction(coaNodes, '0', '', 'All', undefined, transaction, item.amount, item.coa); // update root
        });
      });
    })
      .groupBy((node) => node.transactions.hashCode())
      .flatMap( (coaNodes) => coaNodes.filter((node) => coaNodes.size === 1 || node.parentId === '0' || coaNodes.find((childNode) => childNode.parentId === node.id))); // de-dup orphaned leaves

    // apply maxNodesNum rule
    const coaNodesTrimmed = OrderedMap().withMutations((coaNodes) => {
      const coaNodesByParentId = coaNodesRow.groupBy((node) => node.parentId);
      coaNodesByParentId.forEach((coaNodesGroup, parentId) => {
        const coaNodesGroupSorted = coaNodesGroup.sort((node1, node2) => node1.balance !== node2.balance ? node2.balance - node1.balance : node1.label.localeCompare(node2.label));
        if (coaNodesGroupSorted.size > maxNodesNum) {
          const nodesToKeep = coaNodesGroupSorted.takeLast(maxNodesNum - 1);
          const otherId = `${parentId}-others`;
          const otherNodes = coaNodesGroupSorted
            .skipLast(maxNodesNum - 1)
            .map((node) => node.set('parentId', otherId));
          const otherBalance = otherNodes.reduce((balance, node) => balance + node.balance, 0);
          const otherTransactions = otherNodes.flatMap((nodeEntry) => nodeEntry.transactions);
          const otherCOAs = otherNodes.reduce((coas, node) => coas.merge(node.coas), Set());
          const otherNode = new COANode({
            id: otherId,
            parentId,
            transactions: otherTransactions,
            balance: otherBalance,
            label: 'Other',
            coas: otherCOAs,
          });
          coaNodes.set(otherId, otherNode);
          coaNodes.merge(nodesToKeep);

          coaNodes.merge(otherNodes);
        } else {
          coaNodes.merge(coaNodesGroupSorted);
        }
      });
    });
    return coaNodesTrimmed;
  }
)(
  (state, transactions, maxNodesNum) => `${transactions && transactions.hashCode()}:${maxNodesNum}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 6 }),
  },
);

export const childNodes = createCachedSelector(
  (allNodes) => allNodes,
  (allNodes, nodeId) => nodeId,
  (allNodes, nodeId) => allNodes && allNodes.filter((node) => node.parentId === nodeId),
)(
  (allNodes, nodeId) => `${allNodes && allNodes.hashCode()}:${nodeId}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 12 }),
  },
);

export const cachedNodesFromNode = createCachedSelector(
  (node) => node,
  (node) => node && node.id && OrderedMap([[node.id, node]])
)(
  (node) => `${node && node.id}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 4 }),
  },
);

export const coasForNodes = createSelector(
  (nodes) => nodes,
  (nodes) => Set().withMutations((coas) => nodes && nodes.reduce((coasAccumulator, node) => coasAccumulator.merge(node.coas), coas))
);

export const pathToNode = createSelector(
  (allNodes) => allNodes,
  (allNodes, nodeId) => nodeId,
  (allNodes, nodeId) => allNodes && nodeId && ImmutableList().withMutations((nodePath) => {
    for(let node = allNodes.get(nodeId); node; node = allNodes.get(node.parentId)) {
      nodePath.unshift(node);
    }
  })
);

export const mergeCollections = createCachedSelector(
  (collectionArray) => collectionArray,
  (collectionArray) => {
    const collectionArrayNormalized = collectionArray.filter((collection) => collection);
    return collectionArrayNormalized && collectionArrayNormalized.length ? merge(...collectionArrayNormalized) : undefined ;
  },
)(
  (collectionArray) => collectionArray && collectionArray.map((collection) => collection && collection.hashCode && collection.hashCode()).join(':'),
  {
    cacheObject: new LruCacheObject({ cacheSize: 10 }),
    selectorCreator: createArraysEqualShallowSelector,
  },
);

